import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from '../../ui/toast';
import { Button } from '../../ui';
import { TextInput } from '../../ui/input/text_input/text_input';
import {
  enrollBegin,
  enrollConfirm,
  mfaDisable,
  mfaStatus,
  regenerateRecoveryCodes,
} from '../../utils/api_service/mfa_api';

type Status = {
  enrolled: boolean;
  enrolledAt: string | null;
  recoveryCodesRemaining: number;
  requiredByPolicy: boolean;
};

/** Two-factor authentication management, shown inside Account Settings. */
export const TwoFactorSettings = () => {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<Status | null>(null);
  const [setup, setSetup] = useState<{ secret: string; otpauthUri: string; enrollmentId: string } | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [manageCode, setManageCode] = useState('');
  const [busy, setBusy] = useState(false);

  const refresh = () =>
    mfaStatus().then((r) => r.json()).then(setStatus).catch(() => {}).finally(() => setLoading(false));

  useEffect(() => { refresh(); }, []);

  const startSetup = async () => {
    setRecoveryCodes([]);
    const res = await enrollBegin({});
    const d = await res.json();
    if (!res.ok || !d.success) { toast.error('Could not start setup.', { position: 'bottom-center' }); return; }
    setSetup({ secret: d.secret, otpauthUri: d.otpauthUri, enrollmentId: d.enrollmentId });
  };

  const confirmSetup = async () => {
    if (!setup) return;
    setBusy(true);
    try {
      const res = await enrollConfirm({ enrollmentId: setup.enrollmentId, code });
      const d = await res.json();
      if (!res.ok || !d.success) { toast.error('That code was not accepted.', { position: 'bottom-center' }); return; }
      setRecoveryCodes(d.recoveryCodes || []);
      setSetup(null); setCode('');
      await refresh();
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const res = await mfaDisable(manageCode);
      const d = await res.json();
      if (!res.ok || !d.success) {
        toast.error(d.error === 'mfa_required_by_policy'
          ? 'Your organization requires two-factor authentication, so it cannot be turned off.'
          : 'That code was not accepted.', { position: 'bottom-center' });
        return;
      }
      setManageCode(''); setRecoveryCodes([]);
      toast.success('Two-factor authentication disabled.', { position: 'bottom-center' });
      await refresh();
    } finally { setBusy(false); }
  };

  const regenerate = async () => {
    setBusy(true);
    try {
      const res = await regenerateRecoveryCodes(manageCode);
      const d = await res.json();
      if (!res.ok || !d.success) { toast.error('That code was not accepted.', { position: 'bottom-center' }); return; }
      setRecoveryCodes(d.recoveryCodes || []); setManageCode('');
      await refresh();
    } finally { setBusy(false); }
  };

  const recoveryBlock = recoveryCodes.length > 0 && (
    <div className='mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3'>
      <p className='font-medium text-slate-800 text-sm'>Save your recovery codes</p>
      <p className='text-xs text-slate-500 mb-2'>Each works once if you lose your authenticator. They will not be shown again.</p>
      <ul className='grid grid-cols-2 gap-2 font-mono text-xs'>
        {recoveryCodes.map((c) => <li key={c} className='rounded bg-white px-2 py-1 border'>{c}</li>)}
      </ul>
    </div>
  );

  if (loading) {
    return <div className='flex items-center justify-between'>
      <div><p className='font-medium text-slate-700'>Two-Factor Authentication</p>
      <p className='text-sm text-slate-400'>Loading…</p></div></div>;
  }

  // Setting up: QR (scan) + key fallback + code
  if (setup) {
    return (
      <div>
        <p className='font-medium text-slate-700 mb-2'>Set up an authenticator app</p>
        <div className='flex flex-col sm:flex-row gap-4 items-start'>
          <div className='bg-white p-3 rounded-lg border'>
            <QRCodeSVG value={setup.otpauthUri} size={160} includeMargin />
          </div>
          <div className='flex-1'>
            <p className='text-sm text-slate-600'>Scan this with Google Authenticator, Authy or 1Password, then enter the 6-digit code.</p>
            <p className='text-xs text-slate-400 mt-2'>No scanner? Enter this key manually:</p>
            <div className='rounded bg-slate-100 p-2 font-mono text-xs tracking-widest break-all'>{setup.secret}</div>
            <div className='mt-3 max-w-xs'>
              <TextInput type='text' name='code' label='Authentication code' value={code}
                         onChange={(e) => setCode(e.target.value.trim())} />
            </div>
            <div className='flex gap-2 mt-3'>
              <Button onClick={confirmSetup} disabled={busy} label={busy ? 'Verifying…' : 'Verify and enable'} />
              <button type='button' className='text-sm text-slate-500 underline'
                      onClick={() => { setSetup(null); setCode(''); }}>Cancel</button>
            </div>
          </div>
        </div>
        {recoveryBlock}
      </div>
    );
  }

  // Enrolled
  if (status?.enrolled) {
    return (
      <div>
        <div className='flex items-center justify-between'>
          <div>
            <p className='font-medium text-slate-700'>Two-Factor Authentication</p>
            <p className='text-sm text-emerald-600'>
              On · {status.recoveryCodesRemaining} recovery code{status.recoveryCodesRemaining === 1 ? '' : 's'} left
            </p>
          </div>
        </div>
        <div className='mt-3 flex flex-col sm:flex-row gap-2 sm:items-end'>
          <div className='max-w-xs w-full'>
            <TextInput type='text' name='manageCode' label='Enter a current code to make changes'
                       value={manageCode} onChange={(e) => setManageCode(e.target.value.trim())} />
          </div>
          <Button onClick={regenerate} disabled={busy || !manageCode} label='Regenerate recovery codes' />
          <button type='button' disabled={busy || !manageCode}
                  className='text-sm text-red-600 underline disabled:opacity-40 pb-2' onClick={disable}>
            Turn off
          </button>
        </div>
        {status.requiredByPolicy && <p className='text-xs text-amber-700 mt-2'>Your organization requires two-factor authentication.</p>}
        {recoveryBlock}
      </div>
    );
  }

  // Not enrolled
  return (
    <div className='flex items-center justify-between'>
      <div>
        <p className='font-medium text-slate-700'>Two-Factor Authentication</p>
        <p className='text-sm text-slate-500'>
          {status?.requiredByPolicy ? 'Required by your organization.' : 'Add a second step to your sign-in.'}
        </p>
      </div>
      <Button variant='primary' onClick={startSetup} label='Set up' />
    </div>
  );
};
